import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { PaymentsService } from '../../api/PaymentsAPI';
import s from '../../styles/staff.module.css';

const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const moneyShort = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const fmtApptDate = (v) =>
  v ? new Date(v).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// Admin-only picker to link an order to one of the customer's appointments.
// Uses a fixed-position panel so it isn't clipped by the table's overflow.
export function AppointmentSelect({ row, light, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  const { data } = useQuery({
    queryKey: ['customer-appts', row.customerId],
    queryFn: () => PaymentsService.customerAppointments(row.customerId),
    enabled: open && !!row.customerId,
    staleTime: 60000,
  });
  const appts = data?.appointments || [];

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const GAP = 12;
      // The panel is wider than the button that opens it, so left-aligning it
      // to a trigger near the right edge pushes it off screen — and a fixed
      // element cannot be scrolled back into view. Cap the width to what is
      // actually available, then pull it left until it fits.
      const maxWidth = Math.min(window.innerWidth - GAP * 2, 420);
      const left = Math.max(GAP, Math.min(r.left, window.innerWidth - GAP - maxWidth));

      // Same for the vertical: drop upward when there is more room above.
      const below = window.innerHeight - r.bottom - GAP;
      const above = r.top - GAP;
      const flip = below < 240 && above > below;
      setPos({
        left,
        width: Math.max(r.width, 300),
        maxWidth,
        maxHeight: Math.max(180, Math.min(340, flip ? above : below)),
        ...(flip ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const insideDropdown = (t) => t && t.nodeType === 1 && t.closest && t.closest('[data-appt-dropdown]');
    const onScroll = (e) => { if (!insideDropdown(e.target)) close(); };
    const onDoc = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target) && !insideDropdown(e.target)) close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  const select = (bookId) => { onChange(row.orderId, bookId); setOpen(false); };

  return (
    <>
      <button ref={btnRef} className={s.linkApptBtn} onClick={toggle} disabled={!row.customerId} type="button">
        {row.bookId
          ? `Linked ✓ — ${fmtApptDate(row.appointment?.startAt)} – ${moneyShort(row.appointment?.totalAmount)}`
          : 'Link appointment'}
      </button>
      {open && pos && (
        <div
          data-appt-dropdown
          className={`${s.apptDropdown} ${light ? s.apptDropdownLight : ''}`}
          style={{
            position: 'fixed',
            left: pos.left,
            ...(pos.top != null ? { top: pos.top } : { bottom: pos.bottom }),
            minWidth: Math.min(pos.width, pos.maxWidth),
            maxWidth: pos.maxWidth,
            maxHeight: pos.maxHeight,
          }}
        >
          <div className={s.apptOption} onClick={() => select(null)}>— none —</div>
          {appts.map((a) => (
            <div
              key={a.externalId}
              className={`${s.apptOption} ${a.externalId === row.bookId ? s.apptOptionActive : ''}`}
              onClick={() => select(a.externalId)}
            >
              <div className={s.apptOptRow}><span>{fmtApptDate(a.startAt)}</span><span>{a.locationName || '—'}</span></div>
              <div className={s.apptOptRow}><span>{a.customer || '—'}</span><span>{money(a.totalAmount)}</span></div>
              <div className={s.apptOptLabel}>Services:</div>
              <ul className={s.apptOptList}>
                {(a.services || []).map((sv, i) => <li key={i}>{sv}</li>)}
              </ul>
              <div className={s.apptOptAssigned}>AssignedBy: {a.assignedStaff?.length ? a.assignedStaff.join(', ') : '—'}</div>
            </div>
          ))}
          {!appts.length && <div className={s.apptOption} style={{ opacity: 0.6 }}>No appointments for this customer</div>}
        </div>
      )}
    </>
  );
}
